import { SwapOutlined, UploadOutlined } from '@ant-design/icons';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { SubmitButton } from '@framework/form/form/submit/SubmitButton.tsx';
import { CheckboxInput } from '@framework/form/inputs/CheckboxInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FileUploadInput } from '@framework/form/inputs/upload/FileUploadInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { AttorneyDownloadDelegationFileButton } from '@framework/frontoffice-submission/module/delegation/attorney-file/AttorneyDownloadDelegationFileButton.tsx';
import { type ISubmissionManager } from '@framework/frontoffice-submission/module/delegation/IDelegation.ts';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { DrawerButtonRow } from '@framework/ui/components/drawer/DrawerButtonRow.tsx';
import { generatePath } from '@framework/ui/core/router/TypedRoute.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { Button, message, Space, Tooltip, Typography } from 'antd';
import { type RcFile } from 'antd/es/upload';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddressBookPersonButton } from '#features/packages/common-web/src/address-book/components/button/person/AddressBookPersonButton.tsx';
import { useChangeSubmissionManager } from '../hooks/useChangeSubmissionManager.ts';

const TFormItem = FormItem as typeof FormItem<ChangeSubmissionManagerValues>;

const ChangeSubmissionManagerSchema = yup
  .object()
  .shape({
    fiscalCode: yup.string().required().label('Codice Fiscale'),
    canSubmit: yup.boolean().required().default(false).label('Può Inviare'),
    file: yup
      .mixed<RcFile>()
      // Verifichiamo che il file sia richiesto
      .when('$requiresFile', ([requiresFile], schema) =>
        requiresFile ? schema.required() : schema,
      )
      .label('Documento di Delega'),
    name: yup.string().notRequired().label('Nome'),
    surname: yup.string().notRequired().label('Cognome'),
    email: yup.string().notRequired().email().label('Email'),
    assignAsManager: yup.boolean().default(false).label('Assegna come Gestore'),
  })
  .required()
  .noUnknown();

export type ChangeSubmissionManagerValues = yup.InferType<
  typeof ChangeSubmissionManagerSchema
>;

export interface IChangeSubmissionManagerButtonProps {
  submissionId: string | null;
  type: 'manager' | 'attorney';
  iconOnly?: boolean;
  data: ISubmissionManager | null | undefined;
  requiresFile?: boolean | null;
}

export function ChangeSubmissionManagerButton(
  props: IChangeSubmissionManagerButtonProps,
) {
  const { submissionId, iconOnly, type, data, requiresFile } = props;
  const { t } = useTranslation();
  const { update } = useChangeSubmissionManager(type);

  const subjectName =
    type === 'manager'
      ? t('change-submission-manager-button.subject-name.gestore', 'Gestore')
      : t(
          'change-submission-manager-button.subject-name.procuratore',
          'Procuratore',
        );

  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleSubmit = useCallback(
    async (values: ChangeSubmissionManagerValues) => {
      if (!submissionId) {
        console.error('No submissionId');
        message.error(
          t(
            'change-submission-manager-button.message.error.nessun-id-della-richiesta-trovato',
            'Nessun ID della richiesta trovato',
          ),
        );
        return;
      }
      try {
        const result = await update.mutateAsync(submissionId, values);
        if (!result) return;

        message.success(
          `${subjectName} ${t(
            'change-submission-manager-button.message.success.aggiornato-con-successo',
            'aggiornato con successo',
          )}`,
        );

        setOpen(false);
      } catch (e) {
        console.log(e);
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'change-submission-manager-button.message.error.si-e-verificato-un-errore-riprovare',
              'Si è verificato un errore. Riprovare.',
            ),
          ),
        );
      }
    },
    [submissionId, update, subjectName, t],
  );

  const title = useMemo(() => {
    if (data?.fiscalCode)
      return `${t(
        'change-submission-manager-button.title-prexix.cambia',
        'Cambia',
      )} ${subjectName}`;
    return `${t(
      'change-submission-manager-button.title-prexix.assegna-un',
      'Assegna un',
    )} ${subjectName}`;
  }, [data?.fiscalCode, subjectName, t]);

  const initialValues: ChangeSubmissionManagerValues = useMemo(() => {
    return ChangeSubmissionManagerSchema.cast(data ?? {}, { assert: false });
  }, [data]);

  const validationContext = useMemo(() => {
    return { requiresFile };
  }, [requiresFile]);

  return (
    <>
      <Tooltip title={title}>
        <Button
          icon={<SwapOutlined />}
          onClick={() => {
            setOpen(true);
          }}
          type={iconOnly ? 'link' : 'default'}
        >
          {iconOnly ? undefined : title}
        </Button>
      </Tooltip>
      <Drawer
        title={title}
        open={open}
        onClose={handleClose}
        destroyOnClose
        size="large"
      >
        <FormHandler
          nested
          layout="vertical"
          schema={ChangeSubmissionManagerSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationContext={validationContext}
        >
          <FormErrorsAlert />

          <div style={{ marginBottom: '16px' }}>
            {type === 'manager' && (
              <Typography.Text type="secondary" style={{ marginBottom: 16 }}>
                {t(
                  'change-submission-manager-button.help-description.gestore',
                  "L'utente gestore ha la possibilità di compilare i moduli e delegarne la compilazione e la visualizzazione. L'invio è riservato al titolare o al procuratore in sua vece. Rimuovendoti dal ruolo di gestore, potresti perdere la possibilità di accedere alla richiesta.",
                )}
              </Typography.Text>
            )}

            {type === 'attorney' && (
              <Typography.Text type="secondary">
                {t(
                  'change-submission-manager-button.help-description.procuratore',
                  "L'utente procuratore ha la possibilità di visualizzare la richiesta, delegare i singoli moduli e cambiare il gestore della stessa, facendo le veci del titolare. Solo se viene concesso il permesso, potrà anche inoltrare la richiesta.",
                )}
              </Typography.Text>
            )}
          </div>

          <TFormItem
            name={'fiscalCode'}
            label={t(
              'change-submission-manager-button.input.fiscal-code.label.codice-fiscale',
              'Codice Fiscale o E-Mail',
            )}
          >
            <Space.Compact size="large" block>
              <TextInput
                name={'fiscalCode'}
                placeholder={t(
                  'change-submission-manager-button.input.fiscal-code.placeholder.codice-fiscale',
                  'Codice Fiscale o E-Mail',
                )}
              />
              <AddressBookPersonButton
                idFieldName="fiscalCode"
                addressBookUrl={generatePath('/rubrica')}
                onSelect={(record, close, setValue) => {
                  setValue(record.codiceFiscale ?? '<unknown>');
                  close();
                }}
              />
            </Space.Compact>
          </TFormItem>

          {type === 'attorney' && (
            <>
              <TFormItem
                name="name"
                label={t(
                  'change-submission-manager-button.input.can-submit.label.nome',
                  'Nome',
                )}
              >
                <TextInput name="name" />
              </TFormItem>
              <TFormItem
                name="surname"
                label={t(
                  'change-submission-manager-button.input.can-submit.label.cognome',
                  'Cognome',
                )}
              >
                <TextInput name="surname" />
              </TFormItem>
              <TFormItem
                name="email"
                label={t(
                  'change-submission-manager-button.input.can-submit.label.email',
                  'E-mail',
                )}
              >
                <TextInput name="email" />
              </TFormItem>

              <TFormItem
                name="canSubmit"
                label={t(
                  'change-submission-manager-button.input.can-submit.label.invio-richiesta',
                  'Invio richiesta',
                )}
                help={t(
                  'change-submission-manager-button.input.can-submit.help.permetti-al-procuratore-indicato-di-inviare-la-richiesta',
                  'Permetti al procuratore indicato di inviare la richiesta',
                )}
              >
                <CheckboxInput name="canSubmit" />{' '}
                {t(
                  'change-submission-manager-button.input.can-submit.checkbox-text.permesso-di-invio',
                  'Permesso di invio',
                )}
              </TFormItem>

              <TFormItem
                name="assignAsManager"
                label={t(
                  'change-submission-manager-button.input.can-submit.label.assegna-come-gestore',
                  'Assegna come Gestore',
                )}
                help={t(
                  'change-submission-manager-button.input.can-submit.help.assegna-come-gestore-help',
                  'Il Procuratore potrà procedere alla compilazione della richiesta, all’assegnazione di eventuali delegati o all’inoltro, se abilitato. Può essere modificato anche in seguito.',
                )}
              >
                <CheckboxInput name="assignAsManager" />{' '}
                {t(
                  'change-submission-manager-button.input.can-submit.checkbox-text.assegna-procuratore-come-gestore',
                  'Vuoi indicare il Procuratore come Gestore della Richiesta?',
                )}
              </TFormItem>

              <TFormItem
                name="file"
                label={t(
                  'change-submission-manager-button.input.file.label.documento-di-delega',
                  'Documento di Delega',
                )}
              >
                <FileUploadInput
                  name="file"
                  title={t(
                    'change-submission-manager-button.input.file.upload-button.title.carica-documento-di-delega',
                    'Carica Documento di Delega',
                  )}
                  buttonProps={{
                    icon: (
                      <UploadOutlined
                        aria-hidden
                        aria-label={t(
                          'change-submission-manager-button.input.file.upload-button.icon-aria-label.carica',
                          'Carica',
                        )}
                      />
                    ),
                  }}
                />
              </TFormItem>

              <AttorneyDownloadDelegationFileButton
                submissionId={props.submissionId!}
                fileId={data?.uploadedFileId}
              />
            </>
          )}

          <DrawerButtonRow>
            <Space>
              <Button size="large" onClick={handleClose}>
                {t('common.annulla', 'Annulla')}
              </Button>
              <SubmitButton />
            </Space>
          </DrawerButtonRow>
        </FormHandler>
      </Drawer>
    </>
  );
}
